import { Component, OnDestroy, OnInit, ViewChild } from "@angular/core";
import { AuthService } from "app/auth/auth.service";
import { Events, IonicPage, ModalController, NavParams } from "ionic-angular";
import { BasePage } from "pages/base-page";
import { ConfigurationService } from "providers/configuration/configuration.service";
import { NavBarServiceProvider } from "providers/nav-bar-service/nav-bar-service";
import { PatientBenefitsService } from "providers/patient-charts/patient-benefits.service";
import { ReferralsServiceProvider } from "providers/patient-charts/referrals-service";
import { StateManagementService } from "providers/state-management/state-management.service";
import {
  PatientChartIdChangeSubscription,
  subscribeToPatientChartIdChange,
} from "providers/nav-bar-service/patient-chart-id-change";
import { PatientBenefitsCrudComponent } from "./patient-benefits-crud/patient-benefits-crud";
import { PatientBenefitsTableComponent } from "./patient-benefits-table/patient-benefits-table";

@IonicPage({
  name: "patient-benefits",
  segment: "patient-benefits/:patientId",
})
@Component({
  selector: "patient-benefits",
  templateUrl: "patient-benefits.html",
})
export class PatientBenefitsPage extends BasePage implements OnInit, OnDestroy {
  patientId: number;
  patientChartId: number;
  private _roomAndBoardToggle: boolean = false;
  private _billingRoomAndBoardToggle: boolean = false;
  private _eligibilityToggle: boolean = false;
  private _instantEligibilityToggle: boolean = false;
  showEligibilityTab: boolean = false;
  pageType: string = "";
  // NOVA-1645 — URL :id → re-fetch subscription (see patient-chart-id-change.ts)
  private patientCtxSub: PatientChartIdChangeSubscription;

  @ViewChild(PatientBenefitsCrudComponent)
  patientBenefitsCrud: PatientBenefitsCrudComponent;

  constructor(
    public navParams: NavParams,
    public stateData: StateManagementService,
    public referralsService: ReferralsServiceProvider,
    public patientBenefitsService: PatientBenefitsService,
    public modalController: ModalController,
    public events: Events,
    private authSvc: AuthService,
    private configurationService: ConfigurationService,
    private navBarService: NavBarServiceProvider
  ) {
    super(stateData);
    if (this.referralsService.PATIENT_STATUS) {
      this.pageType = this.referralsService.PATIENT_STATUS;
    }
    this.validateRoomAndBoardToggle();
    this.validateBillingRoomAndBoardToggle();
    this.validateEligibilityToggle();
    this.validateInstantEligibilityToggle();
  }
  ngOnInit(): void {
    this.patientId = +this.navBarService.getPatientChartId();
    this.patientChartId = +this.navBarService.getExternalId();

    // NOVA-1645 — Ionic reuses this page across a hash :id change. The benefits
    // fetch lives in the patient-benefits-crud child and is keyed on the
    // EXTERNAL id, while the URL carries the INTERNAL id — so resolve the new
    // patient via getPatientById(internalId, false) (which also rewrites the
    // sessionStorage id keys) and re-drive the crud child with the resolved
    // external id. Set up here in ngOnInit (not ionViewWillEnter) because this
    // page's Ionic enter hook does not reliably fire on a deep-link/reused
    // instance; torn down in ngOnDestroy.
    if (!this.patientCtxSub) {
      this.patientCtxSub = subscribeToPatientChartIdChange(
        this.events,
        this.navBarService,
        {
          getBoundId: () => this.patientId,
          onChange: (urlId) => {
            this.patientId = urlId;
            this.referralsService
              .getPatientById(urlId, false)
              .subscribe((patient: any) => {
                this.patientChartId =
                  patient && patient.externalId != null
                    ? +patient.externalId
                    : +this.navBarService.getExternalId();
                const crud = this.patientBenefitsCrud;
                if (crud) {
                  crud.patientId = this.patientId;
                  crud.patientChartId = this.patientChartId;
                  crud.retrievePatientBenefitsList();
                }
              });
          },
        }
      );
    }
  }

  ngOnDestroy(): void {
    // NOVA-1645 — tear down with the specific handler ref (Events.unsubscribe
    // by topic alone would drop the brief bar's subscriber too).
    if (this.patientCtxSub) {
      this.patientCtxSub.unsubscribe();
      this.patientCtxSub = undefined;
    }
  }

  @ViewChild(PatientBenefitsTableComponent)
  patientBenefitsTable: PatientBenefitsTableComponent;

  ionViewCanEnter() {
    this.referralsService.ACTIVE_TAB_NAME = "patient-benefits";
    return this.authSvc.authenticated();
  }

  ionViewWillEnter() {
    this.stateData.setSectionId("Benefits");
  }

  get roomAndBoardEnabled(): boolean {
    return this._roomAndBoardToggle;
  }

  get billingRoomAndBoardEnabled(): boolean {
    return this._billingRoomAndBoardToggle;
  }

  get eligibilityEnabled(): boolean {
    return this._eligibilityToggle;
  }

  get instantEligibilityEnabled(): boolean {
    return this._instantEligibilityToggle;
  }

  validateRoomAndBoardToggle(): void {
    this.stateData.roomAndBoard.subscribe((toggleOn) => {
      if (toggleOn !== null) {
        this._roomAndBoardToggle = toggleOn;
      }
    });
  }

  validateEligibilityToggle(): void {
    this.stateData.showEligibilityToggle.subscribe((toggleOn) => {
      if (toggleOn !== null) {
        this._eligibilityToggle = toggleOn;
      }
    });
  }

  validateInstantEligibilityToggle(): void {
    this.stateData.showInstantEligibilityToggle.subscribe((toggleOn) => {
      if (toggleOn !== null) {
        this._instantEligibilityToggle = toggleOn;
      }
    });
  }

  validateBillingRoomAndBoardToggle(): void {
    this.stateData.billingRoomNBoardToggle.subscribe((toggleOn) => {
      if (toggleOn !== null) {
        this._billingRoomAndBoardToggle = toggleOn;
      }
    });
  }

  openLegacyRoomAndBoardPayers() {
    let baseUrl = "";
    if (this.pageType == "patient" || this.pageType == "discharge") {
      baseUrl = `${
        this.configurationService.getConfig().logoutUrl
      }/Clinical/PatientChart/Index/${this.navBarService.getExternalId()}?tab=benefits&IsCurrentPayer=BenefitsRB&tenantId=${
        this.authSvc.getUser().tenantId
      }`;
    } else {
      baseUrl = `${
        this.configurationService.getConfig().logoutUrl
      }/Clinical/Patient/CreatePatient/${this.navBarService.getExternalId()}?IsCurrentPayer=BenefitsRB&tenantId=${
        this.authSvc.getUser().tenantId
      }`;
    }
    window.location.assign(baseUrl);
  }

  showEligibility(value) {
    this.showEligibilityTab = value;
  }
}



// WEBPACK FOOTER //
// ./src/pages/patient-chart/patient-benefits/patient-benefits.ts